import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  Phone, MapPin, ArrowRight, ChevronLeft, CheckCircle2, Star,
  ShieldCheck, Timer, BadgeCheck,
} from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import QuoteForm from "@/components/landing/QuoteForm";
import Testimonials from "@/components/landing/Testimonials";
import {
  CITIES, COUNTY_CONTEXT, getCountyServiceCopy, getLocalizedFaqs,
} from "@/data/cities";
import { CORE_SERVICES, CORE_SERVICE_MAP } from "@/data/services";
import { COMPANY } from "@/data/site";
import {
  setSeo,
  buildLocalBusinessSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildServiceSchema,
} from "@/lib/seo";

export default function CityServicePage() {
  const { citySlug, serviceSlug } = useParams();
  const city = CITIES[citySlug];
  const service = CORE_SERVICE_MAP[serviceSlug];

  useEffect(() => {
    if (city && service) {
      const title = `${service.title} in ${city.name}, MI · Peak Plumbing`;
      const description = `${service.blurb} Peak Plumbing serves ${city.name}, MI (${city.county}) with same-day scheduling and upfront pricing. Call (586) 351-6271.`;
      setSeo({
        title,
        description,
        path: `/service-areas/${city.slug}/${service.slug}`,
        jsonLd: [
          {
            id: "org",
            data: buildLocalBusinessSchema({ areaServed: [`${city.name}, MI`, city.county] }),
          },
          { id: "service", data: buildServiceSchema({ serviceName: service.title, cityName: city.name }) },
          {
            id: "breadcrumbs",
            data: buildBreadcrumbSchema([
              { name: "Home", path: "/" },
              { name: "Service Areas", path: "/service-areas" },
              { name: city.name, path: `/service-areas/${city.slug}` },
              { name: service.shortTitle || service.title, path: `/service-areas/${city.slug}/${service.slug}` },
            ]),
          },
          { id: "faq", data: buildFaqSchema(getLocalizedFaqs(city)) },
        ],
      });
    }
    window.scrollTo(0, 0);
  }, [citySlug, serviceSlug, city, service]);

  if (!city) return <Navigate to="/service-areas" replace />;
  if (!service) return <Navigate to={`/service-areas/${citySlug}`} replace />;

  const countyServiceCopy = getCountyServiceCopy(city, service.slug);
  const county = COUNTY_CONTEXT[city.county];
  const localizedFaqs = getLocalizedFaqs(city);

  // Cross-links to two adjacent services in same city
  const otherServices = CORE_SERVICES.filter((s) => s.slug !== service.slug);
  const idx = CORE_SERVICES.findIndex((s) => s.slug === service.slug);
  const adjacent = [
    CORE_SERVICES[(idx + 1) % CORE_SERVICES.length],
    CORE_SERVICES[(idx + 2) % CORE_SERVICES.length],
  ];

  return (
    <main
      data-testid={`city-service-page-${city.slug}-${service.slug}`}
      className="bg-white"
    >
      <Navbar />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-6 text-sm text-slate-500">
        <Link to="/" className="hover:text-[#A88852]">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/service-areas" className="hover:text-[#A88852]">Service Areas</Link>
        <span className="mx-2">/</span>
        <Link to={`/service-areas/${city.slug}`} className="hover:text-[#A88852]">{city.name}</Link>
        <span className="mx-2">/</span>
        <span className="text-[#1E3A5F] font-semibold">{service.shortTitle || service.title}</span>
      </nav>

      {/* Hero */}
      <section className="relative bg-white">
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-10 pb-16 md:pt-14 md:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-full">
              <MapPin size={12} className="text-[#C5A572]" />
              <span className="text-xs font-semibold tracking-wide text-slate-700">
                {service.title} · {city.name}, MI
              </span>
            </div>

            <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight text-[#1E3A5F] mt-5 leading-[1.02]">
              {service.title} in <span className="text-[#A88852]">{city.name}</span>, MI.
            </h1>

            <p className="text-slate-700 text-lg mt-5 max-w-xl leading-relaxed font-semibold">
              {service.blurb}
            </p>

            {countyServiceCopy && (
              <p className="text-slate-600 text-base md:text-lg mt-4 max-w-2xl leading-relaxed">
                {countyServiceCopy}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-7">
              <a
                data-testid={`city-service-${city.slug}-${service.slug}-call`}
                href={`tel:${COMPANY.phoneTel}`}
                className="btn-cta inline-flex items-center gap-2.5 px-6 py-3.5 text-base"
              >
                <Phone size={18} /> Call {COMPANY.phone}
              </a>
              <a href="#quote" className="btn-outline-dark inline-flex items-center px-6 py-3.5 text-base">
                Schedule {service.shortTitle}
              </a>
            </div>
          </div>

          <div id="quote" className="lg:col-span-5">
            <QuoteForm source={`city-service-${city.slug}-${service.slug}`} />
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#122744] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-14 grid md:grid-cols-3 gap-8">
          {[
            { icon: Timer, label: "Fast dispatch", sub: `Same-day availability in ${city.name}` },
            { icon: ShieldCheck, label: "Fully insured", sub: "On every job" },
            { icon: BadgeCheck, label: "1-year warranty", sub: `On ${service.shortTitle.toLowerCase()} work` },
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-11 h-11 grid place-items-center bg-[#1E3A5F] rounded-sm shrink-0">
                <s.icon size={22} className="text-[#D9BF8C]" />
              </div>
              <div>
                <div className="font-display font-black text-2xl">{s.label}</div>
                <div className="text-slate-300 text-sm mt-0.5">{s.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* What this core service covers (H2/H3 sub-topics) */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="label-eyebrow">What we cover</div>
          <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E3A5F] mt-2 tracking-tight max-w-3xl">
            {service.title} in {city.name} includes:
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {service.covers.map((c, i) => (
              <article
                key={i}
                data-testid={`service-covers-${i}`}
                className="bg-slate-50 border border-slate-200 rounded-md p-6"
              >
                <h3 className="font-display text-lg font-bold text-[#1E3A5F]">{c.name}</h3>
                <p className="text-slate-700 text-sm mt-2 leading-relaxed">{c.detail}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* County-specific challenges for this service */}
      <section className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="label-eyebrow">Local considerations</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E3A5F] mt-2 tracking-tight">
              What makes {city.county} different.
            </h2>
            <p className="text-slate-600 mt-4">
              {county.housing}
            </p>
          </div>
          <div className="lg:col-span-7">
            <h3 className="font-display text-xl font-bold text-[#1E3A5F]">
              Common {city.county} plumbing challenges
            </h3>
            <ul className="mt-4 space-y-3">
              {county.challenges.map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 size={18} className="text-[#A88852] mt-1 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Localized FAQ */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="label-eyebrow">{city.name} FAQ</div>
          <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E3A5F] mt-2 tracking-tight">
            Questions from {city.name} homeowners.
          </h2>
          <dl className="mt-8 space-y-4">
            {localizedFaqs.map((f, i) => (
              <div
                key={i}
                data-testid={`service-faq-${i}`}
                className="bg-slate-50 border border-slate-200 rounded-md p-5"
              >
                <dt className="font-display font-bold text-[#1E3A5F]">{f.q}</dt>
                <dd className="text-slate-700 mt-2 text-sm leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <Testimonials />

      {/* Cross-links: adjacent services + all services in this city */}
      <section className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="label-eyebrow">Other services in {city.name}</div>
          <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E3A5F] mt-2 tracking-tight">
            Related plumbing services in {city.name}, MI.
          </h2>

          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {adjacent.map((s) => (
              <Link
                key={s.slug}
                to={`/service-areas/${city.slug}/${s.slug}`}
                data-testid={`city-service-adjacent-${s.slug}`}
                className="group bg-white border border-slate-200 rounded-md p-5 hover:border-[#C5A572] transition"
              >
                <div className="font-display font-bold text-[#1E3A5F]">{s.title}</div>
                <div className="text-sm text-slate-600 mt-1">{s.blurb}</div>
                <div className="mt-3 flex items-center gap-1 text-orange-600 font-semibold text-sm group-hover:gap-2 transition-all">
                  View {s.shortTitle} in {city.name} <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10">
            <h3 className="font-display text-lg font-bold text-[#1E3A5F] mb-4">
              All core plumbing services in {city.name}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {otherServices.map((s) => (
                <Link
                  key={s.slug}
                  to={`/service-areas/${city.slug}/${s.slug}`}
                  data-testid={`city-service-other-${s.slug}`}
                  className="group text-sm bg-white border border-slate-200 rounded-md px-4 py-3 flex items-center justify-between hover:border-[#C5A572] transition"
                >
                  <span className="font-semibold text-[#1E3A5F]">{s.title}</span>
                  <ArrowRight size={12} className="text-[#A88852] group-hover:translate-x-1 transition" />
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-10">
            <Link
              to={`/service-areas/${city.slug}`}
              className="text-sm font-semibold text-[#A88852] hover:underline inline-flex items-center gap-1"
            >
              <ChevronLeft size={14} /> Back to {city.name} overview
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
